import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Users, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

/**
 * Compact "Find a Coach" banner for the Dashboard. Links to the full /coaches
 * directory. Silently hides when there are zero verified coaches so a fresh
 * install doesn't crowd the dashboard with an empty promotion.
 */
export default function FindCoachBanner() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/coaches/directory")
      .then(({ data }) => {
        if (!cancelled) setCount((data?.coaches || []).length);
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });
    return () => { cancelled = true; };
  }, []);

  if (count === null || count === 0) return null;

  const label = count === 1 ? "1 verified coach" : `${count} verified coaches`;

  return (
    <Link
      to="/coaches"
      className="group block bg-gradient-to-r from-teal-50 to-white dark:from-teal-950/30 dark:to-slate-900 border border-teal-200 dark:border-teal-800 rounded-xl p-4 hover:border-teal-400 transition-colors"
      data-testid="find-coach-banner"
    >
      <div className="flex items-center gap-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white"
          style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
          aria-hidden
        >
          <Users className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-teal-700">
              Find a Coach
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-teal-700">
              <ShieldCheck className="w-3 h-3" /> Verified
            </span>
          </div>
          <div className="font-display font-bold text-base text-slate-900 dark:text-slate-100">
            Browse {label}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Ratings, bios, specialties — pick one that fits your goals.
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-[11px] uppercase tracking-widest font-mono-num font-semibold text-teal-700 group-hover:gap-2 transition-all shrink-0">
          <Sparkles className="w-3 h-3" />
          Explore
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
